'use client'

import React, { useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SubjectProgress, useStudentProgress } from '@/hooks/useStudentProgress'
import { ChevronDown, Info, Target, CheckCircle2, Award, BookOpen, Loader2 } from 'lucide-react'
import { useCertificate, CERT_THRESHOLD } from '@/hooks/useCertificate'
import { useFacilityBranding, useOrgVocab } from '@/contexts/SubdomainBrandingContext'

// ─── Design tokens — aligned with app system ─────────────────────────────────
// Blue is the primary CTA colour throughout the app.
// Sage is used for mastered/success states on the learning hub.
// No standalone purple gradients — those clash with the app's identity.
// ── Dark family (ChildSubjects/ParentDashboard reference) — self-contained ──
const STUDY_BLUE = '#0EA5E9'        // action colour — electric blue
const STUDY_BLUE_LIGHT = '#38BDF8'
const SAGE = '#4ADE80'              // success/mastered
const SAGE_DARK = '#16A34A'
const NAVY = '#F2F5FA'              // INK — primary text
const MUTED = '#9AA7BC'
const BORDER = 'rgba(255,255,255,0.10)'
const WHITE = '#0F1930'             // CARD — dark surfaces (name kept: every WHITE bg flips in one edit)

function getScoreColor(score: number): string {
    if (score >= 90) return SAGE
    if (score >= 70) return STUDY_BLUE
    if (score >= 50) return '#FBBF24'
    return '#FB7185'
}

function getScoreKey(score: number): string {
    if (score >= 90) return 'mastered'
    if (score >= 70) return 'progressing'
    if (score >= 50) return 'developing'
    if (score > 0) return 'needsFocus'
    return 'notStarted'
}

/** Animated SVG ring that draws from 0% to the target score */
function ProgressRing({
    score,
    size = 72,
    strokeWidth = 7,
    color,
}: {
    score: number
    size?: number
    strokeWidth?: number
    color: string
}) {
    const radius = (size - strokeWidth) / 2
    const circumference = 2 * Math.PI * radius
    const progressRef = useRef<SVGCircleElement>(null)

    useEffect(() => {
        if (!progressRef.current) return
        const dash = (score / 100) * circumference
        progressRef.current.style.transition = 'stroke-dashoffset 1s cubic-bezier(0.4,0,0.2,1)'
        progressRef.current.style.strokeDashoffset = `${circumference - dash}`
    }, [score, circumference])

    return (
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={`${color}18`} strokeWidth={strokeWidth} />
            <circle
                ref={progressRef}
                cx={size / 2} cy={size / 2} r={radius}
                fill="none" stroke={color}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference}
            />
        </svg>
    )
}

interface SubjectProgressCardProps {
    subject: SubjectProgress
    index?: number
    pendingReflections?: number[]
}

export default function SubjectProgressCard({ subject, index = 0, pendingReflections }: SubjectProgressCardProps) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const vocab = useOrgVocab()
    const branding = useFacilityBranding()
    const score = Math.round(subject.mastery_score ?? 0)
    const scoreColor = getScoreColor(score)
    const scoreKey = getScoreKey(score)
    const lessonsCount = (() => {
        if (!Array.isArray(subject.lessons_completed)) return 0
        const tags = subject.lessons_completed.filter((t: unknown): t is string => typeof t === 'string')
        const uTags = tags.filter((t: string) => /^u\d+_l\d+$/.test(t))
        return uTags.length > 0 ? uTags.length : new Set(tags).size
    })()
    const [isExpanded, setIsExpanded] = React.useState(false)
    const [logs, setLogs] = React.useState<any[]>([])
    const [logsLoading, setLogsLoading] = React.useState(false)
    const { fetchLessonLogs } = useStudentProgress()

    const { generate, download, isGenerating, error: certError, certificateUrl, reset: resetCert } = useCertificate()

    const handleCertificate = async () => {
        if (certificateUrl) {
            // Already generated — re-download without another round-trip
            download(certificateUrl, `${subject.category_name}-Certificate.pdf`)
            return
        }
        const url = await generate({ category_id: subject.category_id, class_id: subject.class_id })
        if (url) download(url, `${subject.category_name}-Certificate.pdf`)
    }

    const toggleExpand = async (e: React.MouseEvent) => {
        e.stopPropagation()
        const newExpanded = !isExpanded
        setIsExpanded(newExpanded)
        if (newExpanded && logs.length === 0) {
            setLogsLoading(true)
            try {
                const res = await fetchLessonLogs(subject.category_id, subject.class_id)
                setLogs(res)
            } finally {
                setLogsLoading(false)
            }
        }
    }

    const handleContinue = () => {
        if (subject.category_id) {
            let url = `/learn/session?category=${subject.category_id}`
            if (subject.class_id) url += `&class=${subject.class_id}`
            navigate(url)
        }
    }

    const lastActiveDate = subject.last_activity_at
        ? new Date(subject.last_activity_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        : null

    const ctaGradient = score >= 90
        ? `linear-gradient(135deg, ${SAGE}, ${SAGE_DARK})`
        : `linear-gradient(135deg, ${STUDY_BLUE}, ${STUDY_BLUE_LIGHT})`

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.07, duration: 0.5, ease: 'easeOut' }}
            whileHover={{ y: -8, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}
            style={{
                background: WHITE,
                borderRadius: 28,
                padding: '28px',
                border: `1px solid ${BORDER}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.30)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
        >
            {/* Decorative background element */}
            <div style={{
                position: 'absolute', top: -20, right: -20, width: 100, height: 100,
                background: `${scoreColor}08`, borderRadius: '50%', zIndex: 0
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Header Section */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: 18,
                            background: `${STUDY_BLUE}18`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 28, flexShrink: 0,
                            boxShadow: `inset 0 2px 4px ${STUDY_BLUE}20`
                        }}>
                            {subject.category_icon}
                        </div>
                        <div style={{ paddingTop: 4 }}>
                            <h3 style={{
                                fontWeight: 900,
                                color: NAVY,
                                margin: 0,
                                fontSize: 18,
                                lineHeight: 1.2,
                                letterSpacing: '-0.02em',
                                fontFamily: "'Playfair Display', serif"
                            }}>
                                {subject.class_name
                                    ? subject.class_name
                                    : t(`categories.items.${subject.category_slug}.name`, subject.category_name)}
                            </h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                                {subject.enrollment_type === 'self_enrolled' ? (
                                    <span style={{
                                        fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em',
                                        color: '#38bdf8', background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.25)',
                                        padding: '2px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4
                                    }}>
                                        <BookOpen size={10} /> Self Enrolled
                                    </span>
                                ) : (
                                    <span style={{
                                        fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em',
                                        color: '#f59e0b', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)',
                                        padding: '2px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4
                                    }}>
                                        <Award size={10} /> {subject.assignment_mode === 'targeted' ? 'Assigned to You' : `Assigned ${vocab.courseSingular}`}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={{ position: 'relative', width: 68, height: 68, flexShrink: 0 }}>
                        <ProgressRing score={score} size={68} strokeWidth={6} color={scoreColor} />
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: 14, fontWeight: 900, color: scoreColor }}>{score}%</span>
                        </div>
                    </div>
                </div>

                {/* Status Badge */}
                <div style={{ marginBottom: 20 }}>
                    <span style={{
                        fontSize: 10, fontWeight: 900, textTransform: 'uppercase',
                        letterSpacing: '0.1em', color: scoreColor,
                        background: `${scoreColor}10`,
                        padding: '5px 12px', borderRadius: 30,
                        border: `1px solid ${scoreColor}15`
                    }}>
                        {t(`education.subjectCard.${scoreKey}`)}
                    </span>
                </div>

                {/* Advanced Stats Section */}
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 12, marginBottom: 24, padding: '16px',
                    background: 'rgba(255,255,255,0.045)', borderRadius: 20,
                    border: `1px solid ${BORDER}`
                }}>
                    {[
                        { labelKey: 'unit', value: subject.current_unit, icon: '📍' },
                        { labelKey: 'lesson', value: subject.current_lesson, icon: '📖' },
                        { labelKey: 'done', value: lessonsCount, icon: '✅' },
                    ].map(stat => (
                        <div key={stat.labelKey} style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: 12, color: MUTED, margin: '0 0 4px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {t(`education.subjectCard.stats.${stat.labelKey}`)}
                            </p>
                            <p style={{ fontSize: 18, fontWeight: 900, color: NAVY, margin: 0 }}>
                                {stat.value}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Review Prompt or Last Active */}
                <div style={{ marginBottom: 20 }}>
                    <div
                        onClick={toggleExpand}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '8px 0' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Info size={14} color={MUTED} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {t('education.subjectCard.aiInsights')}
                            </span>
                        </div>
                        <ChevronDown size={16} color={MUTED} style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                    </div>

                    <AnimatePresence>
                        {isExpanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                style={{ overflow: 'hidden' }}
                            >
                                <div style={{ paddingTop: 12, paddingBottom: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {/* Real-time Weaknesses */}
                                    {(subject.weak_concepts ?? []).length > 0 && (
                                        <div>
                                            <p style={{ fontSize: 11, fontWeight: 800, color: '#b45309', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                                                <Target size={12} /> {t('education.subjectCard.focusPoints')}
                                            </p>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                {(subject.weak_concepts ?? []).map((c: string) => (
                                                    <span key={c} style={{
                                                        fontSize: 10, background: 'rgba(251,191,36,0.10)', color: '#FBBF24',
                                                        padding: '4px 10px', borderRadius: 10, fontWeight: 700,
                                                        border: '1px solid rgba(251,191,36,0.30)'
                                                    }}>
                                                        {c}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Recent Activity Mini Log */}
                                    <div style={{ background: 'rgba(255,255,255,0.045)', borderRadius: 12, padding: 12, border: `1px solid ${BORDER}` }}>
                                        <p style={{ fontSize: 10, fontWeight: 800, color: MUTED, textTransform: 'uppercase', marginBottom: 8 }}>{t('education.subjectCard.recentHistory')}</p>
                                        {logsLoading ? (
                                            <p style={{ fontSize: 11, color: MUTED }}>{t('education.subjectCard.loadingDetails')}</p>
                                        ) : logs.length === 0 ? (
                                            <p style={{ fontSize: 11, color: MUTED }}>{t('education.subjectCard.noHistory')}</p>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {logs.map((log, i) => (
                                                    <div key={i} style={{ fontSize: 11, color: NAVY, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                                        <div style={{ color: log.metadata?.score !== undefined ? (log.metadata.score >= 80 ? SAGE : '#FBBF24') : STUDY_BLUE, marginTop: 1 }}>
                                                            <CheckCircle2 size={12} />
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 700 }}>{t('education.subjectCard.history.unitLesson', { unit: log.unit_number, lesson: log.lesson_number })}</div>
                                                            {log.metadata?.score !== undefined && <div style={{ color: MUTED }}>{t('education.subjectCard.history.score', { score: log.metadata.score })}</div>}
                                                            {log.metadata?.concept_understood && <div style={{ color: '#4ADE80' }}>{t('education.subjectCard.history.mastered', { concept: log.metadata.concept_understood })}</div>}
                                                            {log.metadata?.weak_concept_flagged && <div style={{ color: '#FB7185' }}>{t('education.subjectCard.history.aiNote', { concept: log.metadata.weak_concept_flagged })}</div>}
                                                            {log.metadata?.strong_areas && log.metadata.strong_areas.length > 0 && (
                                                                <div style={{ color: '#4ADE80' }}>{t('education.subjectCard.history.strengths', { areas: log.metadata.strong_areas.join(', ') })}</div>
                                                            )}
                                                            {log.metadata?.weak_areas && log.metadata.weak_areas.length > 0 && (
                                                                <div style={{ color: '#FB7185' }}>{t('education.subjectCard.history.weaknesses', { areas: log.metadata.weak_areas.join(', ') })}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {!isExpanded && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
                            <p style={{ fontSize: 12, color: MUTED, margin: 0, fontWeight: 500 }}>
                                {lastActiveDate
                                    ? t('education.subjectCard.lastActive', { date: lastActiveDate })
                                    : t('education.subjectCard.lastActiveNever')
                                }
                            </p>
                        </div>
                    )}
                </div>

                {/* Pending Reflections (Homework) Badge */}
                {pendingReflections && pendingReflections.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                        <motion.button
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                const mod = pendingReflections[0];
                                let url = `/learn/session?category=${subject.category_id}`;
                                if (subject.class_id) url += `&class=${subject.class_id}`;
                                url += `&module=${mod}&unit=${mod}&lesson=1&reflection=1`;
                                navigate(url);
                            }}
                            style={{
                                width: '100%', padding: '10px 14px',
                                background: 'rgba(201, 168, 92, 0.12)',
                                border: '1px solid rgba(201, 168, 92, 0.35)',
                                color: '#b48a30',
                                borderRadius: 14,
                                fontWeight: 800, fontSize: 13, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            }}
                        >
                            ✍️ {pendingReflections.length} reflection{pendingReflections.length > 1 ? 's' : ''} to write →
                        </motion.button>
                    </div>
                )}

                {/* Primary CTA — always visible */}
                <motion.button
                    id={`edu-subject-cta-${subject.id}`}
                    whileHover={{ scale: 1.01, filter: 'brightness(1.05)' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleContinue}
                    style={{
                        width: '100%', padding: '16px',
                        background: ctaGradient,
                        color: WHITE, border: 'none', borderRadius: 18,
                        fontWeight: 900, fontSize: 15, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                        boxShadow: score >= 90 ? `0 10px 20px ${SAGE}25` : `0 10px 20px ${STUDY_BLUE}25`,
                        transition: 'all 0.2s',
                    }}
                >
                     {score >= 90
                        ? t('education.subjectCard.cta.review')
                        : score > 0
                            ? t('education.subjectCard.cta.continue')
                            : t('education.subjectCard.cta.start')
                    }
                </motion.button>

                {/* Certificate button — only shown when mastery is 90%+ */}
                {score >= CERT_THRESHOLD && (

                    <div style={{ marginTop: 12 }}>
                        <motion.button
                            id={`edu-subject-cert-${subject.id}`}
                            whileHover={{ scale: 1.01, filter: 'brightness(1.05)' }}
                            whileTap={{ scale: 0.97 }}
                            onClick={handleCertificate}
                            disabled={isGenerating}
                            style={{
                                width: '100%', padding: '13px',
                                background: isGenerating ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #92702a, #C9A84C)',
                                color: isGenerating ? MUTED : '#fff',
                                border: '1px solid #C9A84C44',
                                borderRadius: 18,
                                fontWeight: 800, fontSize: 14, cursor: isGenerating ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                boxShadow: '0 6px 16px rgba(201,168,76,0.20)',
                                transition: 'all 0.2s',
                                opacity: isGenerating ? 0.8 : 1,
                            }}
                        >
                            {isGenerating
                                ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Generating PDF…</>  
                                : certificateUrl
                                    ? <><Award size={15} /> Download Again</>  
                                    : <><Award size={15} /> Get Certificate 🎓</>  
                            }
                        </motion.button>
                        {certError && (
                            <p
                                onClick={resetCert}
                                style={{ fontSize: 11, color: '#FB7185', marginTop: 6, textAlign: 'center', cursor: 'pointer' }}
                                title="Click to dismiss"
                            >
                                ⚠ {certError}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </motion.div>
    )
}
